import type { TaskNode, Slot, Segment, ScheduleResult } from "./types";

// Waterfall scheduling of active leaves laid end-to-end; parents span their leaves.
// When versions are shown, leaves are sequenced by version (configured order, then
// used-but-undefined, then "no version"); otherwise by WBS/display order.
// `orderedLeaves` (optional) supplies a precomputed active-leaf order — e.g. dependency
// order, so prerequisites are laid out first — used both plainly and within version bands.
// Completed leaves are pulled to the front (done first, then not-done), each group keeping
// its incoming order. A 0/empty estimate lays out as a 1-day bar so it stays visible.
// `hideDone` drops done leaves entirely, so the chart starts at the first open task.
export function schedule(
  tree: TaskNode[], versions: string[], orderedLeaves?: TaskNode[], hideDone = false,
): ScheduleResult {
  const map = new Map<string, Slot>();
  const segments: Segment[] = [];
  let cursor = 0;
  const barDur = (l: TaskNode) => (l.estimate > 0 ? l.estimate : 1); // empty/0 → a visible 1-day bar

  // parents span the min..max of their active leaf descendants
  function spanParents(node: TaskNode): Slot | null {
    if (node.disabled) return null;
    if (node.children.length === 0) return map.get(node.id) || null;
    let min = Infinity, max = -Infinity;
    for (const c of node.children) {
      const e = spanParents(c);
      if (e) { min = Math.min(min, e.start); max = Math.max(max, e.end); }
    }
    if (max < min) return null;
    const s: Slot = { start: min, end: max, isParent: true };
    map.set(node.id, s);
    return s;
  }

  // active leaves in the chosen order (dependency order when supplied, else display order)
  let leaves: TaskNode[];
  if (orderedLeaves) {
    leaves = orderedLeaves;
  } else {
    leaves = [];
    (function collect(list: TaskNode[]) {
      for (const n of list) {
        if (n.disabled) continue;
        if (n.children.length === 0) leaves.push(n);
        else collect(n.children);
      }
    })(tree);
  }

  // hide-done: drop completed leaves so the chart starts at the first open task
  if (hideDone) leaves = leaves.filter((l) => !l.done);
  // pull completed work to the left: done leaves first, then not-done — a stable sort, so each
  // group keeps its dependency/version/display order. In version mode this reorders within bands.
  leaves = leaves.slice().sort((a, b) => (b.done ? 1 : 0) - (a.done ? 1 : 0));

  // Always lay leaves out grouped by version (configured order, then used-but-undefined, then
  // none). This is NOT gated on the Ver column being visible: which columns you show is a display
  // preference, and hiding one shouldn't silently re-order the actual schedule. For a plan with no
  // versions at all this is identical to a plain display-order waterfall — every leaf falls in the
  // one "" group — and it emits a single anonymous segment, which the header suppresses rather
  // than labelling (see labelSegments in view.ts).
  {
    const order = versions.slice();
    for (const l of leaves) { const v = l.version || ""; if (v && !order.includes(v)) order.push(v); }
    for (const v of order.concat([""])) {
      const segStart = cursor;
      for (const l of leaves) {
        if ((l.version || "") !== v) continue;
        const start = cursor, dur = barDur(l);
        cursor += dur;
        map.set(l.id, { start, end: start + dur, isParent: false });
      }
      if (cursor > segStart) segments.push({ version: v, start: segStart, end: cursor, days: cursor - segStart });
    }
  }

  tree.forEach(spanParents);
  return { map, segments };
}
